import React, { useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, TextField, MenuItem
} from '@mui/material';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import dayjs from 'dayjs';
import EmpSelectbox from '../common/form/autocomplete/EmpSelectbox';
import { LEAVE_TYPE_LABELS } from '../../containers/attendancemng/AttendMngContainer';

const validationSchema = yup.object({
  usrUuid: yup.string().required('직원을 선택하세요'),
  leaveTyp: yup.string().required('유형을 선택하세요'),
  leaveDtSt: yup.string().required('시작일을 입력하세요'),
  leaveDtEd: yup.string().required('종료일을 입력하세요'),
  leaveDays: yup.number().min(0.5).required('일수를 입력하세요'),
});

// 반차/조퇴는 0.5일 기본값
const DEFAULT_DAYS = {
  HALF_AM: 0.5, HALF_PM: 0.5, EARLY_LEAVE: 0.5,
};

const LeaveInputModal = ({ open, data, onClose, onSave }) => {
  const isEdit = !!data;
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));
  const defaultUsrUuid = useMemo(() => user?.usrUuid || '', [user]);

  const formik = useFormik({
    initialValues: {
      usrUuid: defaultUsrUuid,
      leaveTyp: 'ANNUAL',
      leaveDtSt: dayjs().format('YYYY-MM-DD'),
      leaveDtEd: dayjs().format('YYYY-MM-DD'),
      leaveDays: 1,
      rmk: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      onSave(values);
    },
  });

  // 유형 변경 시 일수 자동 설정
  const handleLeaveTypChange = (e) => {
    const typ = e.target.value;
    formik.setFieldValue('leaveTyp', typ);
    if (DEFAULT_DAYS[typ]) {
      formik.setFieldValue('leaveDays', DEFAULT_DAYS[typ]);
    }
  };

  // 날짜 변경 시 일수 자동 계산
  const handleDateChange = (field, value) => {
    formik.setFieldValue(field, value);
    const st = field === 'leaveDtSt' ? value : formik.values.leaveDtSt;
    const ed = field === 'leaveDtEd' ? value : formik.values.leaveDtEd;
    if (st && ed) {
      const diff = dayjs(ed).diff(dayjs(st), 'day') + 1;
      if (!DEFAULT_DAYS[formik.values.leaveTyp]) {
        formik.setFieldValue('leaveDays', Math.max(1, diff));
      }
    }
  };

  useEffect(() => {
    if (open) {
      if (data) {
        formik.setValues({
          usrUuid: data.usrUuid || '',
          leaveTyp: data.leaveTyp || 'ANNUAL',
          leaveDtSt: data.leaveDtSt ? dayjs(data.leaveDtSt).format('YYYY-MM-DD') : '',
          leaveDtEd: data.leaveDtEd ? dayjs(data.leaveDtEd).format('YYYY-MM-DD') : '',
          leaveDays: data.leaveDays ?? 1,
          rmk: data.rmk || '',
        });
      } else {
        formik.resetForm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? '휴가/근태 수정' : '휴가/근태 등록'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <EmpSelectbox
              formik={formik}
              name="usrUuid"
              label="직원 *"
              variant="outlined"
              margin="none"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select fullWidth size="small" label="유형 *"
              name="leaveTyp"
              value={formik.values.leaveTyp}
              onChange={handleLeaveTypChange}
              error={formik.touched.leaveTyp && Boolean(formik.errors.leaveTyp)}
              helperText={formik.touched.leaveTyp && formik.errors.leaveTyp}
            >
              {Object.entries(LEAVE_TYPE_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth type="date" label="시작일 *" size="small"
              InputLabelProps={{ shrink: true }}
              name="leaveDtSt"
              value={formik.values.leaveDtSt}
              onChange={(e) => handleDateChange('leaveDtSt', e.target.value)}
              error={formik.touched.leaveDtSt && Boolean(formik.errors.leaveDtSt)}
              helperText={formik.touched.leaveDtSt && formik.errors.leaveDtSt}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth type="date" label="종료일 *" size="small"
              InputLabelProps={{ shrink: true }}
              name="leaveDtEd"
              value={formik.values.leaveDtEd}
              onChange={(e) => handleDateChange('leaveDtEd', e.target.value)}
              error={formik.touched.leaveDtEd && Boolean(formik.errors.leaveDtEd)}
              helperText={formik.touched.leaveDtEd && formik.errors.leaveDtEd}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth type="number" label="일수 *" size="small"
              inputProps={{ min: 0.5, step: 0.5 }}
              name="leaveDays"
              value={formik.values.leaveDays}
              onChange={formik.handleChange}
              error={formik.touched.leaveDays && Boolean(formik.errors.leaveDays)}
              helperText={formik.touched.leaveDays && formik.errors.leaveDays}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={2} label="사유/비고" size="small"
              name="rmk"
              value={formik.values.rmk}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">취소</Button>
        <Button onClick={formik.handleSubmit} variant="contained">저장</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveInputModal;
